import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Task } from "../types";
import { useStore } from "../../../helpers";
import { taskQueryKeys } from "../utils/queryKeys";

interface UseTaskMutationsOptions {
    taskId?: number;
    onRefresh?: () => void;
    onClose?: () => void;
    onModeChange?: (mode: "view" | "edit") => void;
    refetchTaskDetails?: () => void;
}

/**
 * Custom hook for task mutations (sync, clone, delete, update)
 * Centralizes React Query mutation logic for task operations
 */
export const useTaskMutations = (options: UseTaskMutationsOptions = {}) => {
    const { taskManagerStore } = useStore();
    const queryClient = useQueryClient();
    const { taskId, onRefresh, onClose, onModeChange, refetchTaskDetails } = options;

    const syncTaskMutation = useMutation(
        async (taskId: number) => {
            const res = await taskManagerStore.refresh_user_tasks(taskId);
            if (res?.status !== 200 && res?.status !== 204) {
                throw new Error("Failed to sync task");
            }
            return res;
        },
        {
            onSuccess: () => {
                toast.success("Task synced successfully");
                queryClient.invalidateQueries({ queryKey: taskQueryKeys.all });
                refetchTaskDetails?.();
                onRefresh?.();
            },
            onError: () => toast.error("Failed to sync task"),
        }
    );

    const cloneTaskMutation = useMutation(
        async (taskId: number) => {
            const res = await taskManagerStore.clone_task(taskId);
            if (res?.status !== 200 && res?.status !== 204) {
                throw new Error("Failed to clone task");
            }
            return res;
        },
        {
            onSuccess: () => {
                toast.success("Task cloned successfully");
                queryClient.invalidateQueries({ queryKey: taskQueryKeys.all });
                onRefresh?.();
                onClose?.();
            },
            onError: () => toast.error("Failed to clone task"),
        }
    );

    const deleteTaskMutation = useMutation(
        async (taskId: number) => {
            const res = await taskManagerStore.delete_task(taskId);
            if (res?.status !== 200 && res?.status !== 204) {
                throw new Error("Failed to delete task");
            }
            return res;
        },
        {
            onSuccess: () => {
                toast.success("Task deleted successfully");
                queryClient.invalidateQueries({ queryKey: taskQueryKeys.all });
                onRefresh?.();
                onClose?.();
            },
            onError: () => toast.error("Failed to delete task"),
        }
    );

    const updateTaskMutation = useMutation(
        async (updatedTask: Task) => {
            taskManagerStore.set_active_task(updatedTask);
            await taskManagerStore.update_task();
        },
        {
            onSuccess: () => {
                toast.success("✓ Task updated successfully");
                queryClient.invalidateQueries({ queryKey: taskQueryKeys.all });
                if (taskId) {
                    queryClient.invalidateQueries({ queryKey: taskQueryKeys.detail(taskId) });
                }
                onRefresh?.();
                // Close drawer if onClose is provided, otherwise switch to view mode
                if (onClose) {
                    onClose();
                } else {
                    onModeChange?.("view");
                }
            },
            onError: () => toast.error("Failed to update task"),
        }
    );

    return {
        syncTaskMutation,
        cloneTaskMutation,
        deleteTaskMutation,
        updateTaskMutation,
    };
};

